/**
 * jobTimelineText — the Queue table's Timing column formatter (WSRPDM-0013,
 * moved unchanged out of the deleted card component by WSRPDM-0043 when the
 * card grid became a table).
 */
import type { QueueJobView } from '@/api/wsr/types';
import { formatSecondsDuration } from './duration';

/**
 * `registry.ts`'s `TFn` is label-only (`(key, fallback?) => string`, no
 * interpolation) — too narrow for the `{{duration}}`/`{{time}}` values this
 * function's translations need. `useCopy()`'s actual `t` (3-arg, values
 * last) satisfies this wider shape structurally, so callers pass it as-is.
 */
type TranslateFn = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

/**
 * The row's time line, in priority order (spec WSRPDM-0013 §2):
 *   1. `runningForSeconds` present → "Running Ns" (the job is actively running).
 *   2. `finishedAt` present → "Finished Ns ago", Ns computed against `generatedAt`.
 *   3. Otherwise → "Scheduled …" from `scheduledFor` (pending/claimed, not yet started).
 * Exported standalone so the three branches get direct unit coverage without
 * mounting a component.
 */
export function jobTimelineText(job: QueueJobView, generatedAt: string, t: TranslateFn): string {
  if (job.runningForSeconds !== null) {
    return t('queue.card.running', 'Running {{duration}}', {
      duration: formatSecondsDuration(job.runningForSeconds),
    });
  }
  if (job.finishedAt !== null) {
    const elapsedSeconds = Math.max(
      0,
      Math.floor((new Date(generatedAt).getTime() - new Date(job.finishedAt).getTime()) / 1000),
    );
    return t('queue.card.finishedAgo', 'Finished {{duration}} ago', {
      duration: formatSecondsDuration(elapsedSeconds),
    });
  }
  return t('queue.card.scheduledFor', 'Scheduled {{time}}', {
    time: new Date(job.scheduledFor).toLocaleString(),
  });
}
